'use client';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, AlertTriangle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BackButton } from '@/components/ui/BackButton';
import { Badge } from '@/components/ui/badge';

import { ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useNavigate } from 'react-router-dom';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { ProcessorEditHeader } from '../../components/processor-edit-header';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';

interface TapEditPageProps {
  processor: ProcessorDetails;
}

const TapEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id!);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-1/3 rounded bg-gray-200"></div>
          <div className="space-y-4">
            <div className="h-32 rounded bg-gray-200"></div>
            <div className="h-32 rounded bg-gray-200"></div>
            <div className="h-32 rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <TapEditPage processor={data} />;
};

const TapEditPage = ({ processor }: TapEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const processorExit = useProcessorEditExit();
  const { orgRoutes } = useOrgNavigate();
  const { toast } = useToast();

  const [validationError, setValidationError] = useState<string | null>(null);

  const updateProcessorMutation = useUpdateProcessor();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    testMode: processor.options?.testMode ?? false,
    dryMode: (processor.options as any)?.dryMode === true,
    apiKey: processor.options?.apiKey || '',
    merchantId: processor.options?.merchantId || '',
    threeDSecure: processor.options?.threeDSecure ?? true,
    saveCard: processor.options?.saveCard ?? false,
    receiptEmail: processor.options?.receiptEmail ?? false,
    receiptSms: processor.options?.receiptSms ?? false,
    descriptorSuffix: processor.options?.descriptor?.descriptor_suffix || '',
  });

  const handleSubmit = async () => {
    // Validation
    if (!formData.apiKey.trim()) {
      setValidationError(t('tap.configuration.apiKeyRequired'));
      return;
    }

    if (!formData.merchantId.trim()) {
      setValidationError(t('tap.configuration.merchantIdRequired'));
      return;
    }

    setValidationError(null);

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: 'tap',
          name: formData.name,
          enabled: formData.enabled,
          options: {
            testMode: formData.testMode,
            dryMode: formData.dryMode,
            apiKey: formData.apiKey,
            merchantId: formData.merchantId,
            threeDSecure: formData.threeDSecure,
            saveCard: formData.saveCard,
            receiptEmail: formData.receiptEmail,
            receiptSms: formData.receiptSms,
            descriptor: {
              descriptor_suffix: formData.descriptorSuffix,
            },
          },
        } as any,
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('tap.editTitle')}
        onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* General Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* API Configuration */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('tap.configuration.apiConfigurationTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="apiKey">{t('tap.configuration.apiKeyLabel')}</Label>
              <Input
                id="apiKey"
                type="password"
                value={formData.apiKey}
                onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                placeholder={formData.testMode ? 'sk_test_...' : 'sk_live_...'}
              />
              <p className="text-muted-foreground mt-1 text-sm">
                {t('tap.configuration.apiKeyDescription')}
              </p>
            </div>

            <div>
              <Label htmlFor="merchantId">{t('tap.configuration.merchantIdLabel')}</Label>
              <Input
                id="merchantId"
                value={formData.merchantId}
                onChange={(e) => setFormData({ ...formData, merchantId: e.target.value })}
                placeholder={t('tap.configuration.merchantIdPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-sm">
                {t('tap.configuration.merchantIdDescription')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Test Mode Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('testModeSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="testMode"
                checked={formData.testMode}
                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
              />
              <Label htmlFor="testMode">{t('enableTestMode')}</Label>
            </div>
            {formData.testMode && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm text-yellow-800">
                  {t('tap.testMode.description')}{' '}
                  <a
                    href="https://developers.tap.company/reference/testing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-900 underline hover:text-yellow-950"
                  >
                    {t('tap.testMode.viewTestCards')}
                  </a>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('tap.security.securitySettingsTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="threeDSecure"
                checked={formData.threeDSecure}
                onCheckedChange={(checked) => setFormData({ ...formData, threeDSecure: checked === true })}
              />
              <Label htmlFor="threeDSecure">
                {t('tap.security.threeDSecureLabel')}
              </Label>
            </div>
            <p className="text-muted-foreground text-sm">
              {t('tap.security.threeDSecureDescription')}
            </p>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="saveCard"
                checked={formData.saveCard}
                onCheckedChange={(checked) => setFormData({ ...formData, saveCard: checked === true })}
              />
              <Label htmlFor="saveCard">
                {t('tap.security.cardSavingLabel')}
              </Label>
            </div>
            <p className="text-muted-foreground text-sm">
              {t('tap.security.cardSavingDescription')}
            </p>
          </CardContent>
        </Card>

        {/* Receipt Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('tap.receipts.receiptSettingsTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="receiptEmail"
                checked={formData.receiptEmail}
                onCheckedChange={(checked) => setFormData({ ...formData, receiptEmail: checked === true })}
              />
              <Label htmlFor="receiptEmail">
                {t('tap.receipts.emailReceiptLabel')}
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="receiptSms"
                checked={formData.receiptSms}
                onCheckedChange={(checked) => setFormData({ ...formData, receiptSms: checked === true })}
              />
              <Label htmlFor="receiptSms">
                {t('tap.receipts.smsReceiptLabel')}
              </Label>
            </div>

            <p className="text-muted-foreground text-sm">
              {t('tap.receipts.receiptDescription')}
            </p>
          </CardContent>
        </Card>

        {/* Statement Descriptor */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('tap.descriptor.statementDescriptorTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="descriptor_suffix">{t('tap.descriptor.descriptorSuffixLabel')}</Label>
              <Input
                id="descriptor_suffix"
                value={formData.descriptorSuffix}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, descriptorSuffix: value });
                }}
                maxLength={12}
                placeholder={t('tap.descriptor.descriptorSuffixPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-sm">
                {t('tap.descriptor.descriptorSuffixDescription')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Supported Regions */}
        <Card>
          <CardHeader>
            <CardTitle>{t('tap.regions.supportedRegionsTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {['Kuwait', 'Saudi Arabia', 'UAE', 'Bahrain', 'Qatar', 'Oman'].map((country) => (
                <Badge key={country} variant="secondary" className="justify-center">
                  {country}
                </Badge>
              ))}
            </div>
            <p className="text-muted-foreground mt-4 text-sm">
              {t('tap.regions.regionsDescription')}
            </p>
          </CardContent>
        </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
      </div>
    </div>
  );
};

export const TapEditPageContainer = withRequiredPermission(TapEditPageContainerInner, PERMISSIONS.PROCESSORS_MANAGE);
export default TapEditPageContainer;
